import { useImperativeHandle, useMemo, useRef, useState } from "react";
import type { ClipboardEvent, KeyboardEvent, Ref } from "react";
import { Textarea } from "@astrum/ui";
import { EmojiAutocomplete } from "../../components/EmojiAutocomplete.js";
import { MentionAutocomplete } from "../../components/MentionAutocomplete.js";
import type { MentionCandidate } from "../../components/MentionAutocomplete.js";
import {
  insertAt,
  insertLink,
  insideCodeFence,
  shortcutFormat,
  toggleLinePrefix,
  toggleWrap,
  wrapCodeBlock,
} from "../../lib/markdown-edit.js";
import type { Edit, FormatAction } from "../../lib/markdown-edit.js";
import { useUserGroups } from "../../lib/user-groups.js";
import type { ChannelMember } from "./types.js";

/**
 * The text field of the composer, with its mention picker.
 *
 * Kept behind this one small contract — a controlled string value, a submit
 * callback, and pasted files handed back — so a rich-text editor can replace
 * the textarea without the composer around it (attachments, emoji, the send
 * button, reply banner) changing. The value is the readable form, `@Name`
 * rather than the wire form; the composer resolves mentions at send time.
 *
 * The text is markdown, and formatting — the toolbar or its shortcuts — is a
 * text edit that adds or removes markers around the selection. What you type
 * is what is stored; there is no hidden rich-text model to fall out of step.
 */

/** What the composer can do to the field from outside it. */
export interface MessageInputHandle {
  /** Puts text at the caret (an emoji from the picker), and focuses the field. */
  insert: (text: string) => void;
}

const TOOLBAR: { action: FormatAction; label: string; glyph: string; shortcut?: string }[] = [
  { action: "bold", label: "Bold", glyph: "B", shortcut: "⌘B" },
  { action: "italic", label: "Italic", glyph: "I", shortcut: "⌘I" },
  { action: "strike", label: "Strikethrough", glyph: "S", shortcut: "⌘⇧X" },
  { action: "code", label: "Code", glyph: "</>", shortcut: "⌘E" },
  { action: "codeBlock", label: "Code block", glyph: "{ }" },
  { action: "link", label: "Link", glyph: "🔗" },
  { action: "quote", label: "Quote", glyph: "❝" },
  { action: "bullet", label: "Bulleted list", glyph: "•", shortcut: "⌘⇧8" },
  { action: "number", label: "Numbered list", glyph: "1.", shortcut: "⌘⇧7" },
];

function applyFormat(action: FormatAction, value: string, start: number, end: number): Edit {
  switch (action) {
    case "bold":
      return toggleWrap(value, start, end, "**");
    case "italic":
      return toggleWrap(value, start, end, "_");
    case "strike":
      return toggleWrap(value, start, end, "~~");
    case "code":
      return toggleWrap(value, start, end, "`");
    case "codeBlock":
      return wrapCodeBlock(value, start, end);
    case "link":
      return insertLink(value, start, end);
    case "quote":
      return toggleLinePrefix(value, start, end, "quote");
    case "bullet":
      return toggleLinePrefix(value, start, end, "bullet");
    case "number":
      return toggleLinePrefix(value, start, end, "number");
  }
}

export interface MessageInputProps {
  value: string;
  onChange: (value: string) => void;
  /** Enter without Shift, while the mention picker is closed. */
  onSubmit: () => void;
  placeholder: string;
  /** Who the mention picker offers. */
  members: ChannelMember[];
  /** Called on each keystroke that leaves text in the field. */
  onTyping: () => void;
  /** Images pasted from the clipboard, already taken out of the paste. */
  onPasteFiles: (files: File[]) => void;
  /** Whether the formatting toolbar is showing; the shortcuts work either way. */
  showToolbar?: boolean;
  handleRef?: Ref<MessageInputHandle>;
}

export function MessageInput({
  value,
  onChange,
  onSubmit,
  placeholder,
  members,
  onTyping,
  onPasteFiles,
  showToolbar = false,
  handleRef,
}: MessageInputProps) {
  /* Where the caret is, so the picker knows which `@word` is being typed. */
  const [caret, setCaret] = useState(0);
  const [mentionOpen, setMentionOpen] = useState(false);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement | null>(null);
  const groups = useUserGroups();

  /* Besides the channel's people: @channel, @here, and the office's groups. */
  const candidates = useMemo<MentionCandidate[]>(
    () => [
      ...members,
      { id: "broadcast:channel", displayName: "channel", jobTitle: "Everyone in this conversation", insert: "channel" },
      { id: "broadcast:here", displayName: "here", jobTitle: "Everyone online right now", insert: "here" },
      ...groups.map((g) => ({
        id: `group:${g.id}`,
        displayName: g.name,
        jobTitle: `@${g.handle} · ${g.memberIds.length} ${g.memberIds.length === 1 ? "person" : "people"}`,
        insert: g.handle,
      })),
    ],
    [members, groups],
  );

  /** Sets a new value and puts the selection back once React has painted it. */
  const commit = (edit: Edit) => {
    onChange(edit.value);
    setCaret(edit.end);
    // Restored after the paint, or the browser puts the caret at the end.
    requestAnimationFrame(() => {
      const node = textareaRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(edit.start, edit.end);
    });
  };

  const format = (action: FormatAction) => {
    const node = textareaRef.current;
    const start = node?.selectionStart ?? value.length;
    const end = node?.selectionEnd ?? value.length;
    commit(applyFormat(action, value, start, end));
  };

  useImperativeHandle(handleRef, () => ({
    insert: (text: string) => {
      const node = textareaRef.current;
      const start = node?.selectionStart ?? value.length;
      const end = node?.selectionEnd ?? value.length;
      commit(insertAt(value, start, end, text));
    },
  }));

  const handleKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    // An open picker owns Enter: the message is not finished, and sending
    // "@div" or ":tad" helps nobody.
    if (
      (mentionOpen || emojiOpen) &&
      (event.key === "Enter" || event.key === "Tab" || event.key === "ArrowUp" || event.key === "ArrowDown")
    ) {
      return;
    }
    const shortcut = shortcutFormat(event);
    if (shortcut) {
      event.preventDefault();
      format(shortcut);
      return;
    }
    // Enter sends; Shift+Enter inserts a newline, matching every mainstream
    // chat client — except inside an open ``` block, where Enter is a newline.
    if (event.key === "Enter" && !event.shiftKey) {
      const node = event.currentTarget;
      if (insideCodeFence(value, node.selectionStart ?? value.length)) return;
      event.preventDefault();
      onSubmit();
    }
  };

  return (
    <div className="chat-composer__field-wrap">
      {showToolbar && (
        <div className="chat-composer__toolbar" role="toolbar" aria-label="Formatting">
          {TOOLBAR.map((tool) => (
            <button
              key={tool.action}
              type="button"
              className={`chat-composer__tool chat-composer__tool--${tool.action}`}
              aria-label={tool.label}
              title={tool.shortcut ? `${tool.label} (${tool.shortcut})` : tool.label}
              // Mouse down, not click: the field keeps its selection.
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => format(tool.action)}
            >
              {tool.glyph}
            </button>
          ))}
        </div>
      )}
      <MentionAutocomplete
        value={value}
        caret={caret}
        people={candidates}
        onOpenChange={setMentionOpen}
        onPick={(next, nextCaret) => commit({ value: next, start: nextCaret, end: nextCaret })}
      />
      <EmojiAutocomplete
        value={value}
        caret={caret}
        onOpenChange={setEmojiOpen}
        onPick={(next, nextCaret) => commit({ value: next, start: nextCaret, end: nextCaret })}
      />
      <Textarea
        ref={textareaRef}
        aria-label="Message"
        placeholder={placeholder}
        value={value}
        maxRows={8}
        onChange={(e) => {
          onChange(e.target.value);
          setCaret(e.target.selectionStart ?? e.target.value.length);
          // Sent on every keystroke; the server collapses them into one
          // broadcast per TTL window, so no debounce is needed here.
          if (e.target.value) onTyping();
        }}
        // Arrow keys and clicks move the caret without changing the text, so
        // the picker would otherwise keep answering about the old position.
        onSelect={(e) => setCaret((e.target as HTMLTextAreaElement).selectionStart ?? 0)}
        onKeyDown={handleKeyDown}
        onPaste={(e: ClipboardEvent<HTMLTextAreaElement>) => {
          // A screenshot or copied image arrives as a file on the
          // clipboard, not as text — handled the same way a dropped or
          // picked file is, and the paste's own text insertion is
          // suppressed so an image paste does not also leave a stray
          // filename typed into the field.
          const imageFiles = Array.from(e.clipboardData.items)
            .filter((item) => item.kind === "file" && item.type.startsWith("image/"))
            .map((item) => item.getAsFile())
            .filter((file): file is File => file !== null);
          if (imageFiles.length === 0) return;
          e.preventDefault();
          onPasteFiles(imageFiles);
        }}
        className="chat-composer__field"
      />
    </div>
  );
}
